/**
 * Acciones PURAS sobre ModelInputs: (inputs) => inputs nuevos. Las usa el store del formulario y se prueban sin React.
 * Invariantes que respetan todas:
 *  - el conjunto de `row` de cada tabla es fijo (las filas del Excel); "quitar" deja la fila en blanco;
 *  - `modality` nunca queda vacía (vacía en el motor = ×1);
 *  - `personalUse` ∈ {"Sí","No",null} exacto; estrategias, buckets, categorías y subcategorías solo con texto canónico;
 *  - null ≠ 0: vacío es null.
 */
import type {
  AssetInput, CadenceInput, ExpenseInput, FilmInput, FilterInput, IncomeInput, LiabilityInput, ModelInputs, NeedsInput,
  ObjectiveInput, Plan30Input, PortfolioBucketKey, PortfolioLineInput, TeamInput,
} from "../../../engine/types";
import { isBlank, norm } from "../../../engine/excel/fns";
import { TEMPLATE_INPUTS } from "../../../engine/versions";

export type Accion = (i: ModelInputs) => ModelInputs;

/** Conceptos de gasto con nombre fijo en la plantilla; los demás ("Otro 1-3" y las filas que el miembro agregó en su Excel) se renombran. */
const CONCEPTOS_FIJOS = new Set(TEMPLATE_INPUTS["wx-final2"].flow.expenses.map((e) => e.concept).filter((c): c is string => !!c && !/^Otro \d$/.test(c)));
export const gastoRenombrable = (r: ExpenseInput): boolean => !CONCEPTOS_FIJOS.has(r.concept ?? "");
const copia = (i: ModelInputs): ModelInputs => JSON.parse(JSON.stringify(i)) as ModelInputs;

// ───────────── Tablas genéricas ─────────────
export interface Tablas {
  incomes: IncomeInput; expenses: ExpenseInput; assets: AssetInput; liabilities: LiabilityInput; objectives: ObjectiveInput;
  film: FilmInput; needs: NeedsInput; team: TeamInput; cadence: CadenceInput; filter: FilterInput; plan30: Plan30Input;
  pres: PortfolioLineInput; inc: PortfolioLineInput; growth: PortfolioLineInput; projects: PortfolioLineInput; contrib: PortfolioLineInput;
}
export type TablaId = keyof Tablas;

export function tabla<T extends TablaId>(i: ModelInputs, t: T): Tablas[T][] {
  switch (t) {
    case "incomes": return i.flow.incomes as Tablas[T][];
    case "expenses": return i.flow.expenses as Tablas[T][];
    case "assets": return i.balance.assets as Tablas[T][];
    case "liabilities": return i.balance.liabilities as Tablas[T][];
    case "objectives": return i.life.objectives as Tablas[T][];
    case "film": return i.life.film as Tablas[T][];
    case "needs": return i.identity.needs as Tablas[T][];
    case "team": return i.system.team as Tablas[T][];
    case "cadence": return i.system.cadence as Tablas[T][];
    case "filter": return i.system.filter as Tablas[T][];
    case "plan30": return i.system.plan30 as Tablas[T][];
    default: return i.portfolio.buckets[t as PortfolioBucketKey] as Tablas[T][];
  }
}

export const fila = <T extends TablaId>(i: ModelInputs, t: T, row: number): Tablas[T] | undefined => tabla(i, t).find((r) => r.row === row);

const limpiarTexto = (v: unknown) => (typeof v === "string" ? (v.trim() === "" ? null : v) : v);

/** Cambia campos de una fila. Normaliza textos vacíos a null y respeta las invariantes por tabla. */
export function setFila<T extends TablaId>(t: T, row: number, patch: Partial<Omit<Tablas[T], "row">>): Accion {
  return (i0) => {
    const i = copia(i0);
    const r = fila(i, t, row) as Record<string, unknown> | undefined;
    if (!r) return i0;
    for (const [k, v] of Object.entries(patch)) r[k] = limpiarTexto(v);
    if ((t === "incomes" || t === "expenses") && isBlank(r.modality)) r.modality = "Mensual";
    if (t === "liabilities" && r.personalUse !== null && r.personalUse !== "Sí" && r.personalUse !== "No") r.personalUse = norm(r.personalUse) === "si" ? "Sí" : "No";
    return i;
  };
}

/** Deja una fila en blanco (conserva `row` y los textos fijos de la plantilla). */
export function vaciarFila<T extends TablaId>(t: T, row: number, plantilla?: Partial<Tablas[T]>): Accion {
  return (i0) => {
    const i = copia(i0);
    const r = fila(i, t, row) as Record<string, unknown> | undefined;
    if (!r) return i0;
    for (const k of Object.keys(r)) if (k !== "row") r[k] = null;
    if (plantilla) Object.assign(r, plantilla);
    if (t === "incomes" || t === "expenses") r.modality = "Mensual";
    return i;
  };
}

/** Primera fila libre de una tabla según un criterio de "ocupada". */
export function filaLibre<T extends TablaId>(i: ModelInputs, t: T, ocupada: (r: Tablas[T]) => boolean): number | null {
  return tabla(i, t).find((r) => !ocupada(r))?.row ?? null;
}

// ───────────── Activos y deudas ─────────────
export const activoOcupado = (a: AssetInput) => !isBlank(a.what) || !isBlank(a.subcategory) || a.value !== null;
export const deudaOcupada = (l: LiabilityInput) => !isBlank(l.what) || l.balance !== null;

/** Agrega un activo en la primera fila libre (6-45). Devuelve la fila o null si ya no hay. */
export function agregarActivo(i: ModelInputs, a: Omit<AssetInput, "row" | "bucketOverride"> & { bucketOverride?: string | null }): { inputs: ModelInputs; row: number | null } {
  const row = filaLibre(i, "assets", activoOcupado);
  if (row === null) return { inputs: i, row: null };
  return { inputs: setFila("assets", row, { bucketOverride: null, ...a })(i), row };
}

export function agregarDeuda(i: ModelInputs, d: Partial<Omit<LiabilityInput, "row">>): { inputs: ModelInputs; row: number | null } {
  const row = filaLibre(i, "liabilities", deudaOcupada);
  if (row === null) return { inputs: i, row: null };
  return { inputs: setFila("liabilities", row, d)(i), row };
}

// ───────────── Objetivos (posición = prioridad) ─────────────
export const objetivoActivo = (o: ObjectiveInput) => !isBlank(o.name);

/** Objetivos con nombre, en el orden de la cascada (prioridad, y la fila como desempate; sin prioridad al final). */
export function objetivosOrdenados(i: ModelInputs): ObjectiveInput[] {
  return i.life.objectives.filter(objetivoActivo).sort((a, b) => ((a.priority ?? 9999) * 100 + a.row) - ((b.priority ?? 9999) * 100 + b.row));
}

/** Reasigna prioridades 1..n según el orden actual (cierra "prioridades repetidas o vacías"). */
export const normalizarPrioridades: Accion = (i0) => {
  const i = copia(i0);
  objetivosOrdenados(i).forEach((o, k) => { fila(i, "objectives", o.row)!.priority = k + 1; });
  return i;
};

/** ¿Las prioridades ya están en 1..n sin repetir? */
export function prioridadesLimpias(i: ModelInputs): boolean {
  const ps = objetivosOrdenados(i).map((o) => o.priority);
  return ps.every((p, k) => p === k + 1);
}

export function agregarObjetivo(i: ModelInputs, o: Partial<Omit<ObjectiveInput, "row" | "priority">> = {}): { inputs: ModelInputs; row: number | null } {
  const libre = i.life.objectives.find((x) => !objetivoActivo(x))?.row ?? null;
  if (libre === null) return { inputs: i, row: null };
  const n = objetivosOrdenados(i).length;
  let out = vaciarFila("objectives", libre)(i);
  out = setFila("objectives", libre, { name: "Nuevo objetivo", ...o, priority: n + 1 })(out);
  return { inputs: normalizarPrioridades(out), row: libre };
}

export const quitarObjetivo = (row: number): Accion => (i) => normalizarPrioridades(vaciarFila("objectives", row)(i));

/** Mueve un objetivo `delta` lugares (−1 = sube). `row` nunca cambia; solo la prioridad. */
export const moverObjetivo = (row: number, delta: number): Accion => (i0) => {
  const orden = objetivosOrdenados(normalizarPrioridades(i0)).map((o) => o.row);
  const k = orden.indexOf(row);
  if (k < 0) return i0;
  const destino = Math.max(0, Math.min(orden.length - 1, k + delta));
  orden.splice(k, 1);
  orden.splice(destino, 0, row);
  const i = copia(i0);
  orden.forEach((r, n) => { fila(i, "objectives", r)!.priority = n + 1; });
  return i;
};

// ───────────── Campos sueltos ─────────────
export const setSetup = (patch: Partial<ModelInputs["setup"]>): Accion => (i0) => { const i = copia(i0); Object.assign(i.setup, patch); if (i.setup.currency !== "USD") i.setup.currency = "MXN"; return i; };
export const setLife = (patch: Partial<Pick<ModelInputs["life"], "emergencyMonths" | "freeCapitalStrategy">>): Accion => (i0) => { const i = copia(i0); Object.assign(i.life, patch); return i; };
export const setStrategy = (patch: Partial<Omit<ModelInputs["strategy"], "concreteAssets">>): Accion => (i0) => { const i = copia(i0); Object.assign(i.strategy, patch); return i; };
export const setIdentity = (patch: Partial<Pick<ModelInputs["identity"], "archetype" | "archetype2" | "trap">>): Accion => (i0) => { const i = copia(i0); Object.assign(i.identity, patch); return i; };
export const setCourage = (patch: Partial<ModelInputs["system"]["courage"]>): Accion => (i0) => { const i = copia(i0); Object.assign(i.system.courage, patch); return i; };
export const setCapital4B = (v: number | null): Accion => (i0) => { const i = copia(i0); i.portfolio.capitalOverride = v; return i; };
/** Fecha (YYYY-MM-DD) en que se contestó el diagnóstico; el siguiente toca ~3 meses después. */
export const setDiagAnsweredAt = (iso: string | null): Accion => (i0) => { const i = copia(i0); i.diag.answeredAt = iso; return i; };

export type Lista = "diag.s1" | "diag.s2" | "diag.s3" | "diag.s4" | "diag.s5" | "diag.wheel.fisica" | "diag.wheel.emocional" | "diag.wheel.relaciones"
  | "identity.story" | "identity.values" | "life.purpose" | "flow.reflections" | "strategy.concreteAssets";
/** Cambia el elemento `k` de un arreglo posicional (sin `row`). La ruta es `grupo.campo` o `grupo.sub.campo`. */
export const setEnLista = (lista: Lista, k: number, v: number | string | null): Accion => (i0) => {
  const i = copia(i0);
  let nodo: unknown = i;
  for (const seg of lista.split(".")) nodo = (nodo as Record<string, unknown>)[seg];
  const arr = nodo as (number | string | null)[] | undefined;
  if (!Array.isArray(arr) || k < 0 || k >= arr.length) return i0;
  arr[k] = typeof v === "string" && v.trim() === "" ? null : v;
  return i;
};

/** Aplica varias acciones en orden. */
export const encadenar = (...as: Accion[]): Accion => (i) => as.reduce((x, f) => f(x), i);
